import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import { API_URL, api, getPosHeaders } from "./api";
import { usePermissions } from "./PermissionContext";
import SaleReturnModal from "./components/SaleReturnModal";
import {
  openSaleReceiptPrintWindow,
  printSaleReceipt,
} from "./printing/SaleReceiptPrinter";

import "./CompanySales.css";

const PAYMENT_METHODS = [
  { value: "cash", label: "Efectivo" },
  { value: "card", label: "Tarjeta" },
  { value: "transfer", label: "Transferencia" },
  { value: "credit", label: "Crédito" },
];

const getProductId = (product) =>
  product.id ?? product.product_id ?? product.product?.id;

const getProductStock = (product) =>
  Number(product.current_stock ?? 0);

const getProductPrice = (product) =>
  Number(
    product.sale_price ??
      product.precio_venta ??
      product.price ??
      product.product?.sale_price ??
      0
  );

const getCartKey = (productId, variantId = null) =>
  `${productId}:${variantId ?? "base"}`;

const getVariantPrice = (product, variant) =>
  Number(variant.sale_price ?? getProductPrice(product));

const getResponseItems = (data) => {
  if (Array.isArray(data)) return data;

  const items = data?.items ?? data?.products ?? data?.data;
  return Array.isArray(items) ? items : [];
};

const normalizeCashSession = (data) => {
  const session =
    data?.session ??
    data?.current_session ??
    data?.cash_session ??
    data?.data ??
    data;

  if (!session) return null;

  const status = String(session.status ?? "").toLowerCase();

  if (
    session.is_open === false ||
    ["closed", "cerrada", "close"].includes(status)
  ) {
    return null;
  }

  const id =
    session.id ?? session.cash_session_id ?? session.session_id;

  if (!id) return null;

  return {
    ...session,
    id,
    opening_amount:
      session.opening_amount ??
      session.initial_amount ??
      session.opening_balance ??
      session.amount_opening ??
      0,
  };
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "Ocurrió un error. Intenta nuevamente.";
};

const normalizeCustomerText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function CompanySales() {
  const { company } = useParams();
  const navigate = useNavigate();

  const barcodeInputRef = useRef(null);
  const keyboardActionsRef = useRef({});
  const saleSubmittingRef = useRef(false);
  const promotionCartRef = useRef(null);

  const { can } = usePermissions();

  const canCreateSales = can("sales.create");
  const canCancelSales = can("sales.cancel");
  const canViewAccounting = can("accounting.view");

  const [posBranding, setPosBranding] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [savingSale, setSavingSale] = useState(false);
  const [cashSession, setCashSession] = useState(null);
  const [, setRecentSales] = useState([]);

  const [saleModal, setSaleModal] = useState(null);
  const [linkedJournalEntry, setLinkedJournalEntry] = useState(null);
  const [variantPicker, setVariantPicker] = useState(null);
  const [loadingVariants, setLoadingVariants] = useState(false);

  const [promotionPreview, setPromotionPreview] = useState(null);
  const [loadingPromotionPreview, setLoadingPromotionPreview] =
    useState(false);
  const [promotionPreviewError, setPromotionPreviewError] =
    useState(null);

  const [payments, setPayments] = useState([
    {
      payment_method: "cash",
      amount: 0,
      received_amount: 0,
    },
  ]);

  const [customerName, setCustomerName] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerMatches, setShowCustomerMatches] =
    useState(false);
  const [creditDueDate, setCreditDueDate] = useState("");
  const [message, setMessage] = useState(null);

  const [productMatches, setProductMatches] = useState([]);
  const [showProductMatches, setShowProductMatches] =
    useState(false);
  const [searchingMatches, setSearchingMatches] = useState(false);
  const [productSearchError, setProductSearchError] = useState("");
  const [activeProductIndex, setActiveProductIndex] = useState(-1);
  const [selectingMatch, setSelectingMatch] = useState(false);

  const getHeaders = useCallback(
    () => getPosHeaders(company),
    [company]
  );

  useEffect(() => {
    const controller = new AbortController();

    setPosBranding(null);

    if (company) {
      api
        .get(
          `/companies/by-subdomain/${encodeURIComponent(
            company
          )}/branding`,
          { signal: controller.signal }
        )
        .then(({ data }) => {
          if (!controller.signal.aborted) {
            setPosBranding({ ...data, subdomain: company });
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            toast.warning(
              "No se pudo cargar la identidad visual. Se usarán los colores predeterminados."
            );
          }
        });
    }

    return () => controller.abort();
  }, [company]);

  const companyTheme = useMemo(() => {
    const validColor = (value, fallback) => {
      if (
        typeof value === "string" &&
        value.trim() &&
        !value.includes("var(") &&
        typeof CSS !== "undefined" &&
        CSS.supports("color", value.trim())
      ) {
        return value.trim();
      }

      return fallback;
    };

    const branding =
      posBranding?.subdomain === company ? posBranding : null;

    const primary = validColor(
      branding?.primary_color,
      "#1f5d7a"
    );
    const secondary = validColor(
      branding?.secondary_color,
      primary
    );

    return {
      "--company-primary": primary,
      "--company-secondary": secondary,
      "--company-primary-dark": secondary,
      "--pos-primary": primary,
      "--pos-primary-dark": secondary,
      "--pos-primary-soft": `color-mix(in srgb, ${primary} 8%, white)`,
      "--pos-primary-border": `color-mix(in srgb, ${primary} 22%, white)`,
      "--pos-primary-focus": `color-mix(in srgb, ${primary} 22%, transparent)`,
    };
  }, [company, posBranding]);

  const subtotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) =>
          sum + Number(item.sale_price) * Number(item.quantity),
        0
      ),
    [cart]
  );

  const promotionIsCurrent =
    promotionPreview !== null &&
    promotionCartRef.current === cart;

  const discount = promotionIsCurrent
    ? Number(promotionPreview.total_discount || 0)
    : 0;

  const total = promotionIsCurrent
    ? Number(promotionPreview.total ?? subtotal)
    : subtotal;

  const paidTotal = payments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0
  );

  const pendingAmount = total - paidTotal;

  const hasCreditPayment = payments.some(
    (payment) => payment.payment_method === "credit"
  );

  const creditAmount = payments
    .filter((payment) => payment.payment_method === "credit")
    .reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0
    );

  const cashAppliedTotal = payments
    .filter((payment) => payment.payment_method === "cash")
    .reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0
    );

  const cashReceivedTotal = payments
    .filter((payment) => payment.payment_method === "cash")
    .reduce(
      (sum, payment) =>
        sum +
        Number(payment.received_amount ?? payment.amount ?? 0),
      0
    );

  const changeAmount = Math.max(
    0,
    cashReceivedTotal - cashAppliedTotal
  );

  const cashAmountPending = Math.max(
    0,
    cashAppliedTotal - cashReceivedTotal
  );

  const loadCashSession = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/pos/cash-register/me/current`,
        { headers: getHeaders() }
      );

      setCashSession(normalizeCashSession(response.data));
    } catch (error) {
      setCashSession(null);
      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
    }
  }, [getHeaders]);

  const loadProducts = useCallback(
    async (searchText = "") => {
      setLoadingProducts(true);

      try {
        const response = await axios.get(
          `${API_URL}/pos/inventory/me`,
          {
            headers: getHeaders(),
            params: {
              search: searchText,
              limit: 50,
              offset: 0,
            },
          }
        );

        setProducts(getResponseItems(response.data));
      } catch (error) {
        setProducts([]);
        setMessage({
          type: "error",
          text: getErrorMessage(error),
        });
      } finally {
        setLoadingProducts(false);
      }
    },
    [getHeaders]
  );

  const loadRecentSales = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/pos/sales/me`,
        {
          headers: getHeaders(),
          params: { limit: 8 },
        }
      );

      setRecentSales(getResponseItems(response.data));
    } catch {
      setRecentSales([]);
    }
  }, [getHeaders]);

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);

    try {
      const response = await axios.get(
        `${API_URL}/pos/customers/me`,
        {
          headers: getHeaders(),
          params: { active_only: true },
        }
      );

      setCustomers(getResponseItems(response.data));
    } catch (error) {
      setCustomers([]);
      console.error(
        "No fue posible cargar clientes:",
        error
      );
    } finally {
      setLoadingCustomers(false);
    }
  }, [getHeaders]);

  const loadInitialData = useCallback(
    () =>
      Promise.all([
        loadCashSession(),
        loadProducts(),
        loadRecentSales(),
        loadCustomers(),
      ]),
    [
      loadCashSession,
      loadProducts,
      loadRecentSales,
      loadCustomers,
    ]
  );

  useEffect(() => {
    loadInitialData();

    const timer = window.setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadInitialData]);

  useEffect(() => {
    if (!message) return;

    if (message.type === "success") {
      toast.success(message.text);
    } else {
      toast.error(message.text);
    }

    setMessage(null);
  }, [message]);

  useEffect(() => {
    if (payments.length !== 1) return;

    setPayments((current) => {
      if (current.length !== 1) return current;

      const payment = current[0];
      const amount = Number(total.toFixed(2));

      const received =
        payment.payment_method !== "cash" ||
        Number(payment.received_amount || 0) === 0 ||
        Number(payment.received_amount) ===
          Number(payment.amount)
          ? amount
          : Number(payment.received_amount);

      if (
        Number(payment.amount) === amount &&
        Number(payment.received_amount) === received
      ) {
        return current;
      }

      return [
        {
          ...payment,
          amount,
          received_amount: received,
        },
      ];
    });
  }, [total, payments.length]);

  const matchingCustomers = useMemo(() => {
    const term = normalizeCustomerText(customerSearch);

    if (!term) return [];

    return customers
      .filter((customer) =>
        normalizeCustomerText(
          `${customer.document_number || ""} ${
            customer.name || ""
          }`
        ).includes(term)
      )
      .slice(0, 8);
  }, [customers, customerSearch]);

  const handleCustomerSearchChange = (event) => {
    setCustomerSearch(event.target.value);
    setCustomerId("");
    setCustomerName("");
    setShowCustomerMatches(true);
  };

  const selectCustomer = (customer) => {
    setCustomerId(String(customer.id));
    setCustomerName(customer.name || "");
    setCustomerSearch(
      `${customer.document_number || ""} - ${
        customer.name || ""
      }`
    );
    setShowCustomerMatches(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    const term = search.trim();

    setProductMatches([]);
    setActiveProductIndex(-1);
    setProductSearchError("");

    if (!showProductMatches || !term) {
      setSearchingMatches(false);
      return () => controller.abort();
    }

    setSearchingMatches(true);

    const timer = window.setTimeout(async () => {
      try {
        const response = await axios.get(
          `${API_URL}/pos/inventory/me`,
          {
            headers: getHeaders(),
            params: {
              search: term,
              limit: 10,
              offset: 0,
            },
            signal: controller.signal,
          }
        );

        if (!controller.signal.aborted) {
          setProductMatches(getResponseItems(response.data));
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setProductMatches([]);
          setProductSearchError(getErrorMessage(error));
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearchingMatches(false);
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [search, showProductMatches, getHeaders]);

  useEffect(() => {
    if (activeProductIndex < 0) return;

    document
      .getElementById(
        `pos-product-match-${activeProductIndex}`
      )
      ?.scrollIntoView({ block: "nearest" });
  }, [activeProductIndex]);
    const addToCart = (product, variant = null) => {
    if (saleSubmittingRef.current) return;

    const productId = getProductId(product);
    const stock = variant
      ? Number(variant.stock ?? 0)
      : getProductStock(product);

    const variantId = variant?.id ?? null;
    const cartKey = getCartKey(productId, variantId);

    const productName =
      product.name ||
      product.product_name ||
      product.product?.name ||
      "Producto";

    const itemName = variant
      ? `${productName} · ${variant.name}`
      : productName;

    if (!productId) {
      setMessage({
        type: "error",
        text: "No fue posible identificar el producto.",
      });
      return;
    }

    if (stock <= 0) {
      setMessage({
        type: "error",
        text: "Este producto no tiene existencias disponibles.",
      });
      return;
    }

    setCart((current) => {
      const existing = current.find(
        (item) => item.cart_key === cartKey
      );

      if (existing) {
        if (existing.quantity >= stock) {
          setMessage({
            type: "error",
            text: "No hay existencias suficientes.",
          });
          return current;
        }

        return current.map((item) =>
          item.cart_key === cartKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [
        ...current,
        {
          cart_key: cartKey,
          id: productId,
          variant_id: variantId,
          variant_name: variant?.name ?? null,
          sku:
            variant?.sku ??
            product.sku ??
            product.code ??
            "Sin código",
          name: itemName,
          category: product.category ?? "Sin categoría",
          sale_price: variant
            ? getVariantPrice(product, variant)
            : getProductPrice(product),
          stock_actual: stock,
          quantity: 1,
        },
      ];
    });
  };

  const addInventoryVariantToCart = (product) => {
    if (!product.variant_id) {
      addToCart(product);
      return;
    }

    const parentProduct = {
      ...product,
      id: product.parent_product_id ?? getProductId(product),
      name: product.parent_product_name ?? product.name,
    };

    const variant = {
      id: product.variant_id,
      name: product.variant_name ?? product.name,
      sku: product.variant_sku ?? product.sku,
      sale_price: getProductPrice(product),
      stock: getProductStock(product),
    };

    addToCart(parentProduct, variant);
  };

  const handleProductSelection = async (product) => {
    if (saleSubmittingRef.current || loadingVariants) return;

    const productId = getProductId(product);

    if (!productId) {
      setMessage({
        type: "error",
        text: "No fue posible identificar el producto.",
      });
      return;
    }

    setLoadingVariants(true);

    try {
      const response = await axios.get(
        `${API_URL}/pos/products/me/${productId}/variants`,
        {
          headers: getHeaders(),
          params: { only_active: true },
        }
      );

      const variants = getResponseItems(response.data);

      if (!variants.length) {
        addToCart(product);
        return;
      }

      setVariantPicker({ product, variants });
    } catch (error) {
      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
    } finally {
      setLoadingVariants(false);
    }
  };

  const selectProductMatch = async (product) => {
    if (
      selectingMatch ||
      loadingVariants ||
      savingSale ||
      saleSubmittingRef.current
    ) {
      return;
    }

    setSelectingMatch(true);
    setShowProductMatches(false);
    setActiveProductIndex(-1);

    try {
      if (product.is_variant && product.variant_id) {
        addInventoryVariantToCart(product);
      } else {
        await handleProductSelection(product);
      }

      setSearch("");
      setProductMatches([]);
      barcodeInputRef.current?.focus();
    } finally {
      setSelectingMatch(false);
    }
  };

  const handleProductSearchKeyDown = (event) => {
    if (event.nativeEvent.isComposing) return;

    if (event.key === "Escape") {
      event.preventDefault();
      setShowProductMatches(false);
      setActiveProductIndex(-1);
      return;
    }

    if (
      event.key === "ArrowDown" ||
      event.key === "ArrowUp"
    ) {
      event.preventDefault();
      setShowProductMatches(true);

      if (!productMatches.length) return;

      setActiveProductIndex((current) => {
        if (event.key === "ArrowDown") {
          return (current + 1) % productMatches.length;
        }

        return current <= 0
          ? productMatches.length - 1
          : current - 1;
      });

      return;
    }

    if (
      event.key === "Enter" &&
      showProductMatches &&
      activeProductIndex >= 0 &&
      productMatches[activeProductIndex]
    ) {
      event.preventDefault();
      selectProductMatch(
        productMatches[activeProductIndex]
      );
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    if (
      loadingProducts ||
      loadingVariants ||
      selectingMatch ||
      saleSubmittingRef.current
    ) {
      return;
    }

    const term = search.trim();

    if (!term) {
      await loadProducts();
      return;
    }

    setLoadingProducts(true);

    try {
      const response = await axios.get(
        `${API_URL}/pos/inventory/me`,
        {
          headers: getHeaders(),
          params: {
            search: term,
            limit: 50,
            offset: 0,
          },
        }
      );

      const items = getResponseItems(response.data);
      setProducts(items);

      const exactMatch = items.find(
        (product) =>
          String(product.sku ?? product.code ?? "")
            .trim()
            .toLowerCase() === term.toLowerCase()
      );

      if (exactMatch) {
        if (
          exactMatch.is_variant &&
          exactMatch.variant_id
        ) {
          addInventoryVariantToCart(exactMatch);
        } else {
          await handleProductSelection(exactMatch);
        }

        setSearch("");
        setProductMatches([]);
        barcodeInputRef.current?.focus();
      } else if (!items.length) {
        setMessage({
          type: "error",
          text: "No se encontraron productos.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
    } finally {
      setLoadingProducts(false);
    }
  };

  const updateQuantity = (cartKey, value) => {
    if (saleSubmittingRef.current) return;

    const quantity = Number(value);

    if (!Number.isFinite(quantity)) return;

    setCart((current) =>
      current
        .map((item) => {
          if (item.cart_key !== cartKey) return item;
          if (quantity <= 0) return null;

          if (quantity > item.stock_actual) {
            setMessage({
              type: "error",
              text: "No hay existencias suficientes.",
            });
            return item;
          }

          return { ...item, quantity };
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (cartKey) => {
    if (saleSubmittingRef.current) return;

    setCart((current) =>
      current.filter((item) => item.cart_key !== cartKey)
    );
  };

  const resetSale = () => {
    setCart([]);
    setCustomerName("");
    setCustomerId("");
    setCustomerSearch("");
    setShowCustomerMatches(false);
    setCreditDueDate("");
    setPayments([
      {
        payment_method: "cash",
        amount: 0,
        received_amount: 0,
      },
    ]);
    setSearch("");
    setProductMatches([]);
    setShowProductMatches(false);
    setActiveProductIndex(-1);
  };

  const clearCart = () => {
    if (saleSubmittingRef.current) return;

    resetSale();
    setMessage(null);
    barcodeInputRef.current?.focus();
  };

  const addPayment = () => {
    if (saleSubmittingRef.current) return;

    setPayments((current) => [
      ...current,
      {
        payment_method: "card",
        amount: 0,
        received_amount: 0,
      },
    ]);
  };

  const updatePayment = (index, field, value) => {
    if (saleSubmittingRef.current) return;

    setPayments((current) =>
      current.map((payment, paymentIndex) => {
        if (paymentIndex !== index) return payment;

        if (field === "payment_method") {
          return {
            ...payment,
            payment_method: value,
            received_amount:
              value === "cash"
                ? Number(
                    payment.received_amount ||
                      payment.amount ||
                      0
                  )
                : Number(payment.amount || 0),
          };
        }

        const numericValue = Number(value || 0);
        if (!Number.isFinite(numericValue)) return payment;

        if (field === "amount") {
          const previousAmount = Number(
            payment.amount || 0
          );
          const previousReceived = Number(
            payment.received_amount || 0
          );

          const shouldSyncReceived =
            payment.payment_method === "cash" &&
            (previousReceived === 0 ||
              previousReceived === previousAmount);

          return {
            ...payment,
            amount: numericValue,
            received_amount: shouldSyncReceived
              ? numericValue
              : previousReceived,
          };
        }

        if (field === "received_amount") {
          return {
            ...payment,
            received_amount: numericValue,
          };
        }

        return payment;
      })
    );
  };

  const removePayment = (index) => {
    if (
      payments.length === 1 ||
      saleSubmittingRef.current
    ) {
      return;
    }

    setPayments((current) =>
      current.filter(
        (_, paymentIndex) => paymentIndex !== index
      )
    );
  };

  const selectMainPaymentMethod = (method) => {
    if (saleSubmittingRef.current) return;

    const amount = Number(total.toFixed(2));

    setPayments([
      {
        payment_method: method,
        amount,
        received_amount: amount,
      },
    ]);
  };

  const getPaymentLabel = (method) =>
    PAYMENT_METHODS.find(
      (item) => item.value === method
    )?.label || method;

  const handleCreateSale = async () => {
    if (saleSubmittingRef.current) return;

    if (!canCreateSales) {
      toast.error(
        "No tienes permiso para registrar ventas."
      );
      return;
    }

    const fail = (text) =>
      setMessage({ type: "error", text });

    if (!cashSession?.id) {
      fail("Debes abrir una caja antes de registrar ventas.");
      return;
    }

    if (!cart.length) {
      fail("Agrega al menos un producto al carrito.");
      return;
    }

    if (
      loadingPromotionPreview ||
      !promotionIsCurrent ||
      promotionPreviewError
    ) {
      fail(
        promotionPreviewError
          ? `No fue posible actualizar promociones: ${promotionPreviewError}`
          : "Espera mientras se actualizan los precios y promociones."
      );
      return;
    }

    if (
      payments.some(
        (payment) =>
          !Number.isFinite(Number(payment.amount)) ||
          Number(payment.amount) <= 0
      )
    ) {
      fail("Cada pago debe tener un valor mayor a cero.");
      return;
    }

    if (creditAmount > 0 && !customerId) {
      fail(
        "Para registrar una venta a crédito debes seleccionar un cliente."
      );
      return;
    }

    if (creditAmount > 0 && !creditDueDate) {
      fail(
        "Indica la fecha de vencimiento para el crédito."
      );
      return;
    }

    if (cashAmountPending > 0.01) {
      fail(
        `Faltan ${money(
          cashAmountPending
        )} por recibir en efectivo.`
      );
      return;
    }

    if (Math.abs(paidTotal - total) > 0.01) {
      fail(
        `El total de pagos debe ser igual a ${money(total)}.`
      );
      return;
    }

    const payload = {
      customer_id: customerId
        ? Number(customerId)
        : null,
      customer_name: customerName.trim() || null,
      credit_due_date:
        creditAmount > 0 ? creditDueDate : null,
      items: cart.map((item) => ({
        product_id: item.id,
        variant_id: item.variant_id || null,
        quantity: Number(item.quantity),
      })),
      payments: payments.map((payment) => ({
        payment_method: payment.payment_method,
        amount: Number(payment.amount),
      })),
    };

    saleSubmittingRef.current = true;
    setSavingSale(true);

    let printWindow;

    try {
      printWindow = openSaleReceiptPrintWindow();

      const saleResponse = await axios.post(
        `${API_URL}/pos/sales/me`,
        payload,
        { headers: getHeaders() }
      );

      const saleId = saleResponse.data.id;
      const reference =
        saleResponse.data.document_number || `#${saleId}`;

      setMessage({
        type: "success",
        text: `Venta ${reference} registrada correctamente.`,
      });

      try {
        const receiptResponse = await axios.get(
          `${API_URL}/pos/sales/${saleId}/receipt`,
          { headers: getHeaders() }
        );

        printSaleReceipt(
          receiptResponse.data,
          printWindow
        );
      } catch (printError) {
        printWindow?.close();

        toast.warning(
          "La venta fue registrada, pero no fue posible generar el recibo."
        );
        console.error(printError);
      }

      resetSale();

      await Promise.all([
        loadCashSession(),
        loadProducts(),
        loadRecentSales(),
      ]);

      window.setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    } catch (error) {
      printWindow?.close();
      fail(getErrorMessage(error));
    } finally {
      saleSubmittingRef.current = false;
      setSavingSale(false);
    }
  };

  keyboardActionsRef.current = {
    createSale: handleCreateSale,
    clearCart,
  };

  useEffect(() => {
    const handleKeyboardShortcuts = (event) => {
      const tagName =
        document.activeElement?.tagName?.toLowerCase();

      if (
        ["input", "textarea", "select"].includes(tagName) &&
        event.key !== "F10"
      ) {
        return;
      }

      if (event.key === "F10") {
        event.preventDefault();
        keyboardActionsRef.current.createSale?.();
      }

      if (event.key === "F3") {
        event.preventDefault();
        keyboardActionsRef.current.clearCart?.();
      }

      if (event.key === "Escape") {
        setSaleModal(null);
        setLinkedJournalEntry(null);
        setVariantPicker(null);
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyboardShortcuts
    );

    return () =>
      window.removeEventListener(
        "keydown",
        handleKeyboardShortcuts
      );
  }, []);

  useEffect(() => {
    promotionCartRef.current = null;
    setPromotionPreview(null);
    setPromotionPreviewError(null);

    if (!cart.length) {
      setLoadingPromotionPreview(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoadingPromotionPreview(true);

    const timer = window.setTimeout(async () => {
      try {
        const response = await axios.post(
          `${API_URL}/pos/promotions/me/preview`,
          {
            items: cart.map((item) => ({
              product_id: item.id,
              variant_id: item.variant_id || null,
              quantity: item.quantity,
            })),
          },
          {
            headers: getHeaders(),
            signal: controller.signal,
          }
        );

        if (!controller.signal.aborted) {
          promotionCartRef.current = cart;
          setPromotionPreview(response.data);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setPromotionPreview(null);
          setPromotionPreviewError(
            getErrorMessage(error)
          );
          console.error(
            "No fue posible cotizar promociones:",
            error.response?.data || error
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingPromotionPreview(false);
        }
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [cart, getHeaders]);

  const promotionByCartKey = useMemo(() => {
    const entries = promotionIsCurrent
      ? promotionPreview?.items || []
      : [];

    return new Map(
      entries.map((item) => [
        getCartKey(
          item.product_id,
          item.variant_id
        ),
        item,
      ])
    );
  }, [promotionPreview, promotionIsCurrent]);
    return (
    <div
      className="company-sales"
      style={companyTheme}
    >
      <section className="sales-header">
        <div>
          <p className="sales-kicker">Punto de venta</p>
          <h1>Ventas POS</h1>
          <p>
            Escanea el código, agrega productos y registra
            el pago.
          </p>
        </div>

        <div
          className={`cash-status ${
            cashSession?.id ? "cash-open" : "cash-closed"
          }`}
        >
          <span className="cash-status-dot" />

          <div>
            <strong>
              {cashSession?.id
                ? "Caja abierta"
                : "Caja cerrada"}
            </strong>

            <small>
              {cashSession?.id
                ? `Apertura: ${money(
                    cashSession.opening_amount
                  )}`
                : "Abre la caja antes de vender"}
            </small>
          </div>
        </div>
      </section>

      <div className="pos-layout pos-modern-layout">
        <section className="pos-catalog pos-modern-catalog">
          <div className="pos-product-autocomplete">
            <form
              className="pos-search pos-modern-search"
              onSubmit={(event) => {
                setShowProductMatches(false);
                setActiveProductIndex(-1);
                handleSearch(event);
              }}
            >
              <span
                className="pos-search-icon"
                aria-hidden="true"
              >
                ▥
              </span>

              <input
                ref={barcodeInputRef}
                id="product-search"
                type="text"
                role="combobox"
                aria-label="Buscar producto por código, SKU o nombre"
                aria-autocomplete="list"
                aria-expanded={
                  showProductMatches &&
                  Boolean(search.trim())
                }
                aria-controls="pos-product-match-list"
                aria-activedescendant={
                  showProductMatches &&
                  activeProductIndex >= 0 &&
                  productMatches[activeProductIndex]
                    ? `pos-product-match-${activeProductIndex}`
                    : undefined
                }
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setProductMatches([]);
                  setProductSearchError("");
                  setActiveProductIndex(-1);
                  setShowProductMatches(true);
                }}
                onFocus={() => {
                  if (
                    search.trim() &&
                    !selectingMatch
                  ) {
                    setShowProductMatches(true);
                  }
                }}
                onBlur={() => {
                  setShowProductMatches(false);
                  setActiveProductIndex(-1);
                }}
                onKeyDown={handleProductSearchKeyDown}
                placeholder="Buscar producto por código de barras, SKU o nombre..."
                autoComplete="off"
                disabled={selectingMatch || savingSale}
              />

              <button
                type="submit"
                disabled={
                  loadingProducts ||
                  selectingMatch ||
                  loadingVariants ||
                  savingSale
                }
              >
                {loadingProducts
                  ? "Buscando…"
                  : "⌕ Buscar"}
              </button>
            </form>

            {showProductMatches && search.trim() && (
              <div className="pos-product-dropdown">
                <div className="pos-product-dropdown-heading">
                  <strong>
                    Resultados de búsqueda
                  </strong>
                  <span>
                    {productMatches.length} encontrados
                  </span>
                </div>

                <div
                  id="pos-product-match-list"
                  role="listbox"
                  aria-label="Productos encontrados"
                  aria-busy={searchingMatches}
                  className="pos-product-match-list"
                >
                  {productMatches.map(
                    (product, index) => {
                      const name =
                        product.name ||
                        product.product_name ||
                        product.product?.name ||
                        "Producto";

                      const code =
                        product.sku ||
                        product.code ||
                        "-";

                      const stock =
                        getProductStock(product);

                      return (
                        <div
                          id={`pos-product-match-${index}`}
                          key={`${getProductId(
                            product
                          )}:${
                            product.variant_id ??
                            "base"
                          }:${index}`}
                          role="option"
                          aria-selected={
                            activeProductIndex === index
                          }
                          className={`pos-product-match ${
                            activeProductIndex === index
                              ? "pos-product-match-active"
                              : ""
                          }`}
                          onMouseDown={(event) => {
                            event.preventDefault();
                          }}
                          onClick={() =>
                            selectProductMatch(product)
                          }
                        >
                          <span className="pos-product-match-avatar">
                            {String(name)
                              .charAt(0)
                              .toUpperCase()}
                          </span>

                          <div className="pos-product-match-info">
                            <strong>{name}</strong>
                            <small>
                              Código: {code}
                              {product.is_variant
                                ? " · Variante"
                                : ""}
                            </small>
                          </div>

                          <div className="pos-product-match-price">
                            <strong>
                              {money(
                                getProductPrice(product)
                              )}
                            </strong>

                            <small
                              className={
                                stock > 0
                                  ? "pos-match-stock"
                                  : "pos-match-no-stock"
                              }
                            >
                              Stock: {stock}
                            </small>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>

                {searchingMatches && (
                  <p
                    className="pos-product-search-status"
                    role="status"
                  >
                    Buscando productos…
                  </p>
                )}

                {productSearchError && (
                  <p
                    className="pos-product-search-status"
                    role="alert"
                  >
                    {productSearchError}
                  </p>
                )}

                {!searchingMatches &&
                  !productSearchError &&
                  productMatches.length === 0 && (
                    <p
                      className="pos-product-search-status"
                      role="status"
                    >
                      No se encontraron productos.
                    </p>
                  )}

                <div className="pos-product-dropdown-footer">
                  ↑ ↓ Seleccionar · Enter Agregar · Esc
                  Cerrar
                </div>
              </div>
            )}
          </div>

          <section className="pos-cart-table-card">
            <div className="pos-cart-table-heading">
              <div>
                <span>Venta en curso</span>
                <h2>
                  {cart.length
                    ? `${cart.length} producto(s) en el carrito`
                    : "Carrito de venta"}
                </h2>
              </div>

              {cart.length > 0 && (
                <button
                  type="button"
                  className="btn-clear-cart"
                  onClick={clearCart}
                  disabled={savingSale}
                >
                  Vaciar carrito
                </button>
              )}
            </div>

            <div className="pos-cart-table-wrap">
              {cart.length === 0 ? (
                <div className="pos-empty-sale">
                  <span>🛒</span>
                  <strong>
                    Tu venta está lista para comenzar
                  </strong>
                  <p>
                    Busca un producto o selecciónalo en
                    productos frecuentes.
                  </p>
                </div>
              ) : (
                <table className="pos-cart-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Código</th>
                      <th>Producto</th>
                      <th>Cantidad</th>
                      <th>Precio unit.</th>
                      <th>Subtotal</th>
                      <th aria-label="Eliminar" />
                    </tr>
                  </thead>

                  <tbody>
                    {cart.map((item, index) => {
                      const promotion =
                        promotionByCartKey.get(
                          item.cart_key
                        );

                      const baseUnitPrice = Number(
                        promotion?.base_unit_price ??
                          item.sale_price
                      );

                      const finalUnitPrice = Number(
                        promotion?.final_unit_price ??
                          item.sale_price
                      );

                      const lineTotal = Number(
                        promotion?.total ??
                          finalUnitPrice *
                            Number(item.quantity || 0)
                      );

                      return (
                        <tr key={item.cart_key}>
                          <td>{index + 1}</td>
                          <td>{item.sku}</td>

                          <td>
                            <strong>{item.name}</strong>
                            <small>{item.category}</small>

                            {promotion?.promotion_name && (
                              <div className="sale-promotion-detail">
                                <span className="sale-promotion-badge">
                                  {
                                    promotion.promotion_name
                                  }
                                </span>
                                <span className="sale-discount-value">
                                  Descuento: -{" "}
                                  {money(
                                    promotion.discount_total
                                  )}
                                </span>
                              </div>
                            )}
                          </td>

                          <td>
                            <div className="quantity-control">
                              <button
                                type="button"
                                disabled={savingSale}
                                onClick={() =>
                                  updateQuantity(
                                    item.cart_key,
                                    item.quantity - 1
                                  )
                                }
                                aria-label={`Disminuir ${item.name}`}
                              >
                                −
                              </button>

                              <input
                                type="number"
                                min="1"
                                max={item.stock_actual}
                                value={item.quantity}
                                disabled={savingSale}
                                aria-label={`Cantidad de ${item.name}`}
                                onChange={(event) =>
                                  updateQuantity(
                                    item.cart_key,
                                    event.target.value
                                  )
                                }
                              />

                              <button
                                type="button"
                                disabled={savingSale}
                                onClick={() =>
                                  updateQuantity(
                                    item.cart_key,
                                    item.quantity + 1
                                  )
                                }
                                aria-label={`Aumentar ${item.name}`}
                              >
                                +
                              </button>
                            </div>
                          </td>

                          <td>
                            {promotion?.promotion_name ? (
                              <div className="sale-price-with-promotion">
                                <del>
                                  {money(baseUnitPrice)}
                                </del>
                                <strong>
                                  {money(finalUnitPrice)}
                                </strong>
                              </div>
                            ) : (
                              money(finalUnitPrice)
                            )}
                          </td>

                          <td>
                            <b>{money(lineTotal)}</b>
                          </td>

                          <td>
                            <button
                              type="button"
                              className="btn-remove-item"
                              disabled={savingSale}
                              onClick={() =>
                                removeFromCart(
                                  item.cart_key
                                )
                              }
                              title={`Eliminar ${item.name}`}
                              aria-label={`Eliminar ${item.name}`}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="pos-frequent-products">
            <div className="catalog-heading">
              <div>
                <h2>Productos frecuentes</h2>
                <span>
                  Selecciona un producto para agregarlo a
                  la venta.
                </span>
              </div>

              <button
                type="button"
                className="btn-refresh-products"
                disabled={savingSale || loadingProducts}
                onClick={() => {
                  setSearch("");
                  setShowProductMatches(false);
                  loadProducts();
                }}
              >
                ↻ Actualizar
              </button>
            </div>

            <div className="product-grid pos-frequent-grid">
              {loadingProducts ? (
                <p className="catalog-empty">
                  Cargando productos...
                </p>
              ) : products.length === 0 ? (
                <p className="catalog-empty">
                  No hay productos disponibles.
                </p>
              ) : (
                products
                  .slice(0, 10)
                  .map((product) => {
                    const stock =
                      getProductStock(product);
                    const productId =
                      getProductId(product);
                    const productName =
                      product.name ??
                      product.product_name ??
                      "Producto";

                    return (
                      <button
                        type="button"
                        className="pos-product-card"
                        key={`${productId}:${
                          product.variant_id ?? "base"
                        }`}
                        onClick={() => {
                          if (
                            product.is_variant &&
                            product.variant_id
                          ) {
                            addInventoryVariantToCart(
                              product
                            );
                          } else {
                            handleProductSelection(
                              product
                            );
                          }
                        }}
                        disabled={
                          loadingVariants ||
                          selectingMatch ||
                          savingSale
                        }
                      >
                        <span className="pos-product-avatar">
                          {String(productName)
                            .charAt(0)
                            .toUpperCase()}
                        </span>

                        <strong>{productName}</strong>
                        <small>
                          {product.sku ??
                            product.code ??
                            "Sin código"}
                        </small>

                        <div className="pos-product-footer">
                          <b>
                            {money(
                              getProductPrice(product)
                            )}
                          </b>
                          <span
                            className={
                              stock <= 0
                                ? "out-of-stock"
                                : ""
                            }
                          >
                            Stock: {stock}
                          </span>
                        </div>
                      </button>
                    );
                  })
              )}
            </div>
          </section>
        </section>
                <aside className="pos-cart pos-modern-checkout">
          <div className="checkout-heading">
            <span>Resumen de la venta</span>
            <h2>Total a cobrar</h2>
          </div>

          <div className="customer-field customer-search-field">
            <label htmlFor="customer-search">
              Cliente registrado{" "}
              <span>
                {hasCreditPayment
                  ? "(obligatorio para crédito)"
                  : "(opcional)"}
              </span>
            </label>

            <div className="customer-search-box">
              <input
                id="customer-search"
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={
                  showCustomerMatches &&
                  Boolean(customerSearch.trim())
                }
                aria-controls="customer-match-list"
                autoComplete="off"
                value={customerSearch}
                disabled={savingSale}
                onChange={handleCustomerSearchChange}
                onFocus={() =>
                  setShowCustomerMatches(true)
                }
                onBlur={() =>
                  setShowCustomerMatches(false)
                }
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setShowCustomerMatches(false);
                  }

                  if (
                    event.key === "Enter" &&
                    showCustomerMatches &&
                    matchingCustomers.length > 0
                  ) {
                    event.preventDefault();
                    selectCustomer(
                      matchingCustomers[0]
                    );
                  }
                }}
                placeholder="Buscar por documento o nombre"
              />

              {showCustomerMatches &&
                customerSearch.trim() && (
                  <div
                    id="customer-match-list"
                    className="customer-search-results"
                    role="listbox"
                  >
                    {loadingCustomers ? (
                      <p>Cargando clientes...</p>
                    ) : matchingCustomers.length > 0 ? (
                      matchingCustomers.map(
                        (customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            role="option"
                            aria-selected={
                              customerId ===
                              String(customer.id)
                            }
                            onMouseDown={(event) =>
                              event.preventDefault()
                            }
                            onClick={() =>
                              selectCustomer(customer)
                            }
                          >
                            <strong>
                              {customer.name}
                            </strong>
                            <small>
                              {
                                customer.document_number
                              }
                            </small>
                          </button>
                        )
                      )
                    ) : (
                      <p>
                        No se encontraron clientes.
                      </p>
                    )}
                  </div>
                )}
            </div>

            <small className="customer-search-hint">
              {!customerSearch.trim()
                ? "En blanco: Cliente general."
                : customerId
                  ? "Cliente registrado seleccionado."
                  : "Selecciona una coincidencia de la lista."}
            </small>
          </div>

          <div className="customer-field">
            <label htmlFor="customer-name">
              Nombre para el recibo{" "}
              <span>(opcional)</span>
            </label>

            <input
              id="customer-name"
              type="text"
              value={customerName}
              disabled={savingSale}
              onChange={(event) =>
                setCustomerName(event.target.value)
              }
              placeholder="Cliente general"
              readOnly={Boolean(customerId)}
            />
          </div>

          {hasCreditPayment && (
            <div className="customer-field">
              <label htmlFor="credit-due-date">
                Fecha de vencimiento del crédito
              </label>

              <input
                id="credit-due-date"
                type="date"
                value={creditDueDate}
                disabled={savingSale}
                min={new Date()
                  .toISOString()
                  .split("T")[0]}
                onChange={(event) =>
                  setCreditDueDate(event.target.value)
                }
                required
              />

              <small>
                Se generará una cuenta por cobrar para
                el cliente seleccionado.
              </small>
            </div>
          )}

          <div className="checkout-totals">
            <div>
              <span>Subtotal</span>
              <strong>{money(subtotal)}</strong>
            </div>

            {discount > 0 && (
              <div className="checkout-discount">
                <span>Descuentos</span>
                <strong>- {money(discount)}</strong>
              </div>
            )}

            <div className="checkout-total-payable">
              <span>Total a pagar</span>
              <strong>{money(total)}</strong>
            </div>

            {loadingPromotionPreview && (
              <small className="promotion-loading">
                Actualizando promociones…
              </small>
            )}

            {promotionPreviewError && (
              <small className="promotion-error">
                No fue posible calcular promociones:{" "}
                {promotionPreviewError}
              </small>
            )}
          </div>

          <section className="pos-payment-methods">
            <h3>Método de pago</h3>

            <div className="payment-method-buttons">
              {PAYMENT_METHODS.map((method) => (
                <button
                  type="button"
                  key={method.value}
                  disabled={savingSale}
                  className={
                    payments.length === 1 &&
                    payments[0].payment_method ===
                      method.value
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    selectMainPaymentMethod(
                      method.value
                    )
                  }
                >
                  <span>
                    {method.value === "cash"
                      ? "💵"
                      : method.value === "card"
                        ? "💳"
                        : method.value === "transfer"
                          ? "↔"
                          : "📒"}
                  </span>
                  {method.label}
                </button>
              ))}
            </div>
          </section>

          <section className="payment-section pos-modern-payment-section">
            <div className="payment-heading">
              <div>
                <span>Pago recibido</span>
                <h3>
                  {payments.length > 1
                    ? "Pago mixto"
                    : getPaymentLabel(
                        payments[0]?.payment_method
                      )}
                </h3>
              </div>

              <button
                type="button"
                onClick={addPayment}
                disabled={savingSale}
              >
                + Pago mixto
              </button>
            </div>

            <div className="payment-list">
              {payments.map((payment, index) => (
                <div
                  className="payment-row"
                  key={index}
                >
                  <div className="payment-control">
                    <label
                      htmlFor={`payment-method-${index}`}
                    >
                      Medio de pago
                    </label>

                    <select
                      id={`payment-method-${index}`}
                      value={payment.payment_method}
                      disabled={savingSale}
                      onChange={(event) =>
                        updatePayment(
                          index,
                          "payment_method",
                          event.target.value
                        )
                      }
                    >
                      {PAYMENT_METHODS.map(
                        (method) => (
                          <option
                            key={method.value}
                            value={method.value}
                          >
                            {method.label}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div className="payment-control">
                    <label
                      htmlFor={`payment-amount-${index}`}
                    >
                      Valor aplicado
                    </label>

                    <input
                      id={`payment-amount-${index}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={payment.amount}
                      disabled={savingSale}
                      onChange={(event) =>
                        updatePayment(
                          index,
                          "amount",
                          event.target.value
                        )
                      }
                    />
                  </div>

                  {payment.payment_method ===
                    "cash" && (
                    <div className="payment-control payment-received">
                      <label
                        htmlFor={`payment-received-${index}`}
                      >
                        Cliente entrega
                      </label>

                      <input
                        id={`payment-received-${index}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          payment.received_amount
                        }
                        disabled={savingSale}
                        onChange={(event) =>
                          updatePayment(
                            index,
                            "received_amount",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  )}

                  {payments.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove-payment"
                      disabled={savingSale}
                      onClick={() =>
                        removePayment(index)
                      }
                      title="Eliminar medio de pago"
                      aria-label="Eliminar medio de pago"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          <div className="payment-checkout-summary">
            <div>
              <span>Total aplicado</span>
              <strong>{money(paidTotal)}</strong>
            </div>

            {cashAppliedTotal > 0 && (
              <>
                <div>
                  <span>Recibido en efectivo</span>
                  <strong>
                    {money(cashReceivedTotal)}
                  </strong>
                </div>

                <div className="payment-change">
                  <span>Vuelto a entregar</span>
                  <strong>
                    {money(changeAmount)}
                  </strong>
                </div>
              </>
            )}
          </div>

          <p
            className={`payment-difference ${
              Math.abs(pendingAmount) <= 0.01 &&
              cashAmountPending <= 0.01
                ? "balanced"
                : ""
            }`}
          >
            {Math.abs(pendingAmount) <= 0.01 &&
            cashAmountPending <= 0.01
              ? "✓ Pago completo"
              : pendingAmount > 0
                ? `Falta por aplicar: ${money(
                    pendingAmount
                  )}`
                : pendingAmount < 0
                  ? `Excede el total: ${money(
                      Math.abs(pendingAmount)
                    )}`
                  : `Faltan ${money(
                      cashAmountPending
                    )} en efectivo`}
          </p>

          {canCreateSales && (
            <button
              type="button"
              className="btn-charge"
              onClick={handleCreateSale}
              disabled={
                savingSale ||
                loadingPromotionPreview ||
                !promotionIsCurrent ||
                Boolean(promotionPreviewError) ||
                cart.length === 0 ||
                !cashSession?.id
              }
            >
              {savingSale
                ? "Procesando venta..."
                : `🛒 Cobrar ${money(total)} (F10)`}
            </button>
          )}

          <div className="checkout-secondary-actions">
            <button
              type="button"
              onClick={clearCart}
              disabled={
                cart.length === 0 || savingSale
              }
            >
              🗑 Vaciar (F3)
            </button>

            <button
              type="button"
              disabled={savingSale}
              onClick={() =>
                barcodeInputRef.current?.focus()
              }
            >
              ⌕ Buscar producto
            </button>
          </div>
        </aside>
      </div>

      {canCancelSales && saleModal && (
        <SaleReturnModal
          sale={saleModal.sale}
          mode={saleModal.mode}
          onClose={() => setSaleModal(null)}
          onCompleted={async (successMessage) => {
            setSaleModal(null);

            setMessage({
              type: "success",
              text: successMessage,
            });

            await Promise.all([
              loadProducts(),
              loadRecentSales(),
              loadCashSession(),
            ]);
          }}
        />
      )}

      {linkedJournalEntry && (
        <div
          className="sale-linked-entry-backdrop"
          onClick={() =>
            setLinkedJournalEntry(null)
          }
        >
          <section
            className="sale-linked-entry-modal"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <h3>Comprobante contable asociado</h3>

            <p>
              <strong>Número:</strong>{" "}
              {linkedJournalEntry.number}
            </p>

            <p>
              <strong>Fecha:</strong>{" "}
              {new Date(
                `${linkedJournalEntry.entry_date}T00:00:00`
              ).toLocaleDateString("es-CO")}
            </p>

            <p>
              <strong>Descripción:</strong>{" "}
              {linkedJournalEntry.description}
            </p>

            <p>
              <strong>Estado:</strong>{" "}
              {linkedJournalEntry.status}
            </p>

            <div className="sale-linked-entry-actions">
              {canViewAccounting && (
                <button
                  type="button"
                  className="sale-action-btn sale-action-accounting"
                  onClick={() =>
                    navigate(
                      `/${encodeURIComponent(
                        company
                      )}/contabilidad`
                    )
                  }
                >
                  Abrir Contabilidad
                </button>
              )}

              <button
                type="button"
                className="sale-action-btn"
                onClick={() =>
                  setLinkedJournalEntry(null)
                }
              >
                Cerrar
              </button>
            </div>
          </section>
        </div>
      )}

      {variantPicker && (
        <div
          className="variant-picker-overlay"
          onClick={() => setVariantPicker(null)}
        >
          <section
            className="variant-picker-modal"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="variant-picker-header">
              <div>
                <span>SELECCIONAR VARIANTE</span>
                <h2>
                  {variantPicker.product.name ||
                    variantPicker.product.product_name}
                </h2>
                <p>
                  Selecciona la presentación que deseas
                  agregar a la venta.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setVariantPicker(null)
                }
                aria-label="Cerrar selector de variantes"
              >
                ×
              </button>
            </div>

            <div className="variant-picker-list">
              {variantPicker.variants.map(
                (variant) => (
                  <button
                    type="button"
                    key={variant.id}
                    className="variant-picker-option"
                    disabled={
                      Number(variant.stock || 0) <=
                        0 || savingSale
                    }
                    onClick={() => {
                      addToCart(
                        variantPicker.product,
                        variant
                      );
                      setVariantPicker(null);
                      barcodeInputRef.current?.focus();
                    }}
                  >
                    <div>
                      <strong>{variant.name}</strong>
                      <small>{variant.sku}</small>
                    </div>

                    <div>
                      <b>
                        {money(
                          getVariantPrice(
                            variantPicker.product,
                            variant
                          )
                        )}
                      </b>

                      <span>
                        Stock:{" "}
                        {Number(variant.stock || 0)}
                      </span>
                    </div>
                  </button>
                )
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanySales;